import { useEffect, useState } from 'react';

import { normalizeId } from '../utils/ids';
import {
	acquireAttachmentVariantUrl,
	acquireHeldFileVariantUrl,
	hasAttachmentVariant,
	peekAttachmentVariantUrl,
} from '../utils/attachmentVariants';

/**
 * The object URL of one server-made size of an attachment, through the
 * shared cache in `utils/attachmentVariants` (one fetch per attachment and
 * size, held while this component shows it, released on unmount).
 *
 * Asks nothing unless the attachment lists that size in `variants` and both
 * ids are known — pass `attachment: null` to ask nothing at all (a closed
 * viewer). `failed` is the caller's cue to fall back to the file chip.
 *
 * `{ heldIn: collectionId }` (and no item id) reads a HELD file — an upload
 * the item form has not saved yet — through the held route (PR 10b).
 */
export default function useAttachmentVariant(itemId, attachment, size, { heldIn = null, attempt = 0 } = {}) {
	const resolvedItemId = normalizeId(itemId);
	const heldCollectionId = resolvedItemId ? '' : normalizeId(heldIn);
	const attachmentId = normalizeId(attachment?._id);
	const wanted = Boolean((resolvedItemId || heldCollectionId) && attachmentId && hasAttachmentVariant(attachment, size));
	const key = wanted ? `${attachmentId}:${size}` : null;

	const [state, setState] = useState({ key: null, url: null, failed: false });

	useEffect(() => {
		if (!key) return undefined;
		if (attempt > 0) setState({ key: null, url: null, failed: false });
		let active = true;
		const handle = heldCollectionId
			? acquireHeldFileVariantUrl(heldCollectionId, attachmentId, size)
			: acquireAttachmentVariantUrl(resolvedItemId, attachmentId, size);
		handle.promise.then(
			(url) => { if (active) setState({ key, url: url || null, failed: !url }); },
			() => { if (active) setState({ key, url: null, failed: true }); }
		);
		return () => {
			active = false;
			handle.release();
		};
	}, [key, resolvedItemId, heldCollectionId, attachmentId, size, attempt]);

	if (!key) return { url: null, failed: false, loading: false };
	// An answer for the previous attachment never shows under this one; a URL
	// already in the cache shows on the first render, with no chip flash.
	const current = state.key === key ? state : { url: peekAttachmentVariantUrl(attachmentId, size), failed: false };
	return {
		url: current.url,
		failed: current.failed,
		loading: !current.url && !current.failed,
	};
}
